import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GoToStep1, GoToStep2 } from '../actions/app.js';
import { emptyCart } from '../api/cart.js';
import { FullScreenLoader } from '../components/FullScreenLoader.js';
import { Translator } from '../components/Translator.js';
import { useCart } from '../hooks/useCart';
import { useStep } from '../hooks/useStep';
import { cx } from '../utils/cx';
import { isCheckout } from '../utils/wp.js';
import { Cart } from './Cart';

function Step({isActive, onClick, children}) {
    const className = cx(
        'relative block hover:no-underline w-7 h-7 rounded-full border border-headertext flex items-center justify-center',
        isActive ? 'bg-headertext text-header' : 'bg-header text-headertext',
    );

    return (
        <button className={className} onClick={onClick}>
            {children}
        </button>
    );
}

export function Header() {
    const isBackingFromCheckout = useRef(false);

    const eventUrl = useSelector(state => state.cart.event_url);

    const step = useStep();

    const [isCartVisible, setIsCartVisible] = useState(false);
    const [isReloadingTranslations, setIsReloadingTranslations] = useState(false);

    const prices = useCart();

    const dispatch = useDispatch();

    const onCloseCart = useCallback(
        () => setIsCartVisible(false),
        [setIsCartVisible],
    );

    async function onClickStep(clickedStep) {
        if (isBackingFromCheckout.current) {
            return;
        }

        if (clickedStep === 1) {
            dispatch(GoToStep1());
        } else if (clickedStep === 2) {
            dispatch(GoToStep2());
        }

        if (isCheckout()) {
            isBackingFromCheckout.current = true;

            emptyCart()
                .then(() => {
                    document.location.href = eventUrl;
                })
                .catch(() => null);
        }
    }

    useEffect(() => {
        function onClickCheckoutBack() {
            onClickStep(2);
        }

        document.addEventListener('checkoutback', onClickCheckoutBack);

        return () => {
            document.removeEventListener('checkoutback', onClickCheckoutBack);
        };
    }, []);

    async function onChangeLanguage() {
        setIsReloadingTranslations(true);

        try {
            await emptyCart();
        } catch (ex) {}

        localStorage.removeItem('persist:cart');
        localStorage.removeItem('persist:app');

        if (isCheckout()) {
            document.location.href = eventUrl;
        } else {
            const url = new URL(document.location.href);
            if (url.searchParams.has('l')) {
                url.searchParams.delete('l');
            }

            document.location.href = url.toString();
        }
    }

    return (
        <>
            <FullScreenLoader visible={isReloadingTranslations} />
            <header className="p-4 md:p-6 flex items-center justify-between font-bold bg-header maxxsm:text-[10px]">
                <div className="icon-combinr text-[32px] text-headertext md:w-44 maxxsm:text-[28px]"></div>
                <div className="steps relative inline-flex gap-x-2 mx-2 sm:gap-x-8 maxxsm:gap-x-1 maxxsm:mx-1 before:block before:content-[''] before:w-full before:h-[1px] before:absolute before:top-1/2">
                    <Step
                        isActive={step === 1}
                        onClick={() => onClickStep(1)}
                    >
                        1
                    </Step>
                    <Step
                        isActive={step === 2}
                        onClick={() => onClickStep(2)}
                    >
                        2
                    </Step>
                    <Step isActive={step === 3}>3</Step>
                </div>
                <div className="md:w-44 flex justify-end">
                    <button
                        className="inline-flex items-center gap-x-2 rounded-full px-3 py-1.5 bg-headertext text-header"
                        title="Varukorg"
                        onClick={() => setIsCartVisible(true)}
                    >
                        <i className="icon-cart text-[16px]"></i>
                        <span
                            className="whitespace-nowrap"
                            translate="no"
                        >
                            {prices.price} SEK
                        </span>
                    </button>
                    <Cart visible={isCartVisible} onClose={onCloseCart} />
                    <Translator onChangeLanguage={onChangeLanguage} />
                </div>
            </header>
        </>
    );
}
